'use client';

import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Box, Dialog, InputBase, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageVisit, prunePageUsage, readPageUsage, recordPageVisit, splitByUsage } from '@/lib/app/goToPageUsage';
import { buildPageDestinations, PageDestination } from '@/lib/app/navigationItems';

const SHIFT_WINDOW_MS = 400;

const isPublicPath = (pathname: string) => pathname === '/login' || pathname === '/accept-invitation' || pathname.startsWith('/item/');

const destinationSection = (href: string) => new URLSearchParams(href.split('?')[1] ?? '').get('section');

const destinationPathname = (href: string) => href.split('?')[0];

const isCurrentDestination = (destination: PageDestination, pathname: string, section: string | null) => {
  const path = destinationPathname(destination.href);
  const targetSection = destinationSection(destination.href);
  if (targetSection) return pathname === path && section === targetSection;
  if (path === '/settings') return pathname === '/settings' && !section;
  return pathname === path;
};

const matchesQuery = (destination: PageDestination, query: string) => {
  const haystack = `${destination.group ?? ''} ${destination.name} ${destination.keywords ?? ''}`.toLowerCase();
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return tokens.every((token) => haystack.includes(token));
};

const rank = (destination: PageDestination, query: string) => {
  const normalized = query.toLowerCase().trim();
  const name = destination.name.toLowerCase();
  const group = `${destination.group ?? ''} ${destination.keywords ?? ''}`.toLowerCase();
  if (name.startsWith(normalized)) return 0;
  if (name.includes(normalized)) return 1;
  if (group.startsWith(normalized)) return 2;
  return 3;
};

const detailFor = (destination: PageDestination) => [destination.group, destination.keywords].filter(Boolean).join(' · ');

export default function GoToPage() {
  const { user } = useCurrentUser();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [usage, setUsage] = useState<PageVisit[]>([]);
  const listRef = useRef<HTMLUListElement>(null);
  const section = searchParams.get('section');
  const enabled = Boolean(user) && !isPublicPath(pathname);

  const destinations = useMemo(() => (user ? buildPageDestinations(user.permissions) : []), [user]);

  const results = useMemo(() => {
    const { pinned, rest } = splitByUsage(destinations, usage);
    const notCurrent = (destination: PageDestination) => !isCurrentDestination(destination, pathname, section);
    if (!query.trim()) return [...pinned, ...rest].filter(notCurrent);
    const matchingPinned = pinned.filter((destination) => notCurrent(destination) && matchesQuery(destination, query));
    const matchingRest = rest.filter((destination) => notCurrent(destination) && matchesQuery(destination, query)).sort((left, right) => rank(left, query) - rank(right, query) || left.name.localeCompare(right.name));
    return [...matchingPinned, ...matchingRest];
  }, [destinations, query, usage, pathname, section]);

  useEffect(() => {
    if (!user) return;
    setUsage(readPageUsage(user.user_id));
  }, [user]);

  useEffect(() => {
    if (!user || !destinations.length) return;
    const allowed = new Set(destinations.map((destination) => destination.href));
    const pruned = prunePageUsage(user.user_id, allowed, usage);
    if (pruned !== usage) setUsage(pruned);
  }, [user, destinations, usage]);

  useEffect(() => {
    if (!enabled) return;

    let lastShift = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.key !== 'Shift') {
        lastShift = 0;
        return;
      }

      const now = Date.now();
      if (now - lastShift <= SHIFT_WINDOW_MS) {
        lastShift = 0;
        setOpen((current) => !current);
        return;
      }
      lastShift = now;
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled]);

  const scrollToIndex = (index: number) => {
    requestAnimationFrame(() => {
      listRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: 'nearest' });
    });
  };

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    scrollToIndex(0);
  }, [open]);

  const openDestination = (destination: PageDestination) => {
    if (user) setUsage(recordPageVisit(user.user_id, destination.href));
    setOpen(false);
    router.push(destination.href);
  };

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!results.length) return;
      const nextIndex = Math.min(activeIndex + 1, results.length - 1);
      setActiveIndex(nextIndex);
      scrollToIndex(nextIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex = Math.max(activeIndex - 1, 0);
      setActiveIndex(nextIndex);
      scrollToIndex(nextIndex);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const destination = results[activeIndex];
      if (destination) openDestination(destination);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  if (!enabled) return null;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-label="Go to page"
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{
        paper: {
          sx: {
            mt: '12vh',
            borderRadius: 2,
            backgroundImage: 'none',
            overflow: 'hidden',
          },
        },
      }}
    >
      <InputBase
        autoFocus
        fullWidth
        placeholder="Go to a page"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={onInputKeyDown}
        sx={{
          px: 2,
          py: 1.5,
          fontSize: '1.05rem',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />
      <List
        ref={listRef}
        dense
        disablePadding
        sx={{
          maxHeight: 'min(50vh, 420px)',
          overflowY: 'auto',
          py: 0.5,
        }}
      >
        {results.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No matching pages
            </Typography>
          </Box>
        )}
        {results.map((destination, index) => {
          const current = isCurrentDestination(destination, pathname, section);
          const detail = detailFor(destination);
          return (
            <ListItemButton
              key={destination.href}
              data-index={index}
              selected={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => {
                if (event.button !== 0) return;
                event.preventDefault();
                openDestination(destination);
              }}
              sx={{
                py: 0.75,
                px: 2,
                '&.Mui-selected': {
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' },
                },
              }}
            >
              <ListItemText
                primary={destination.name}
                secondary={detail}
                primaryTypographyProps={{ fontWeight: current ? 700 : 500, fontSize: '0.95rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              {current && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2, flexShrink: 0 }}>
                  Current
                </Typography>
              )}
            </ListItemButton>
          );
        })}
      </List>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          px: 2,
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Double-tap Shift
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ↑↓ navigate
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Enter open
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Esc close
        </Typography>
      </Box>
    </Dialog>
  );
}
